import { ReadonlyVec3, vec3 } from 'gl-matrix';
import {
    domainEventBus,
    ROI_CONTOUR_EDIT_EVENT_TYPES,
    RoiContourEditEvent,
} from '~/domain/events';
import { DomainEventSubscription } from '~/domain/events/DomainEventBus';
import { Observable, Observer } from '~/engine/core/observable';
import type { PlaneMm } from '~/domain/geometry/patientGeometry';
import type { WorldBoundsMm } from '~/domain/services/imageService/gridGeometry';
import { Grid } from '~/engine/data/common/Grid';
import type { Environment } from '~/engine/data/Environment';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import {
    RegionOfInterest,
    StagedTextureOptions,
    TextureWrite,
} from '~/engine/data/structure/RegionOfInterest';
import {
    resolveImageSlice,
    resolveTextureSlice,
} from '~/engine/data/structure/planeSliceResolution';
import type { Tool } from '~/engine/tool/Tool';
import {
    assertGlWorkLanded,
    discardPendingGlErrors,
} from '~/engine/util/gl/GLUtilities';
import type { EnvironmentViews } from '~/engine/view/EnvironmentViews';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { viewShowsAcquiredPrimarySlice } from '~/engine/view/planeViewGeometry';
import { ViewManager } from '~/engine/view/ViewManager';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { extractSlicePolygons } from '~/engine/core/utils/marchingSquares';
import {
    blendSlicesBSpline,
    CONTOUR_AUTO_SUGGESTION_OPERATION,
    CONTOUR_AUTO_SUGGESTION_PREVIEW_DELAY_MS,
    NEAREST_CONTOUR_COPY_OPERATION,
    SliceWithPosition,
} from './sliceBlending';
import {
    ContouredRangePosition,
    ContourOperations,
} from '~/engine/util/algorithms/sdf/ContourOperations';
import {
    EdgeRefinementOptions,
    estimateRobustIntensitySpread,
    extractIntensitySliceAlignedToGrid,
    refineSliceWithIntensityEdge,
    refineSliceWithIntensityRegion,
    RegionRefinementOptions,
    scaleRefinementOptionsForSpread,
} from '~/engine/util/algorithms/sdf/IntensityRefinement';
import { Modality } from '~/domain/dicom/modality';
import {
    ContourAutoSuggestionPreviewRenderer,
    ContourAutoSuggestionPreviewState,
    outlineShowsOn,
    previewOutlineMm,
} from './ContourAutoSuggestionPreviewRenderer';
import { sameElements } from '~/utils/array';
import { lightenColor } from '~/utils/colorUtils';
import { CatalogMessageId, localization } from '~/i18n';
import { errorName } from '~/utils/errors';
import { enqueueSnackbar } from '~/utils/notifications';
import { isTextEntryElement } from '~/utils/textEntry';

/** A suggestion on offer, as the controller publishes it. */
export interface PendingContourSuggestion {
    /** The ghost's outline: closed loops in patient coordinates (mm). */
    readonly outlineMm: ReadonlyArray<ReadonlyArray<ReadonlyVec3>>;
    readonly boundsMm: WorldBoundsMm;
    /** The views the ghost is drawn on. */
    readonly views: ReadonlyArray<PlaneView>;
}

type ActiveSuggestion = Omit<PendingContourSuggestion, 'views'> & {
    roi: RegionOfInterest;
    /** In the image's grid, which the texture moving does not change. */
    imageSliceZ: number;
    kind: 'interpolated' | 'copied';
    /**
     * The exact pixel data the renderer used to produce the
     * displayed ghost — a blend of the neighbouring slices, or a
     * verbatim copy of the nearest contoured one. {@link accept}
     * writes these bytes so the committed contour matches what the
     * user saw.
     */
    sourceSliceBytes: Uint8Array;
    /** The ghost as drawn, so a view that joins while it is pending shows it. */
    preview: ContourAutoSuggestionPreviewState;
};

/** Where a suggestion lands in the current texture, extended as needed. */
type DerivedSuggestion = Pick<
    ActiveSuggestion,
    'roi' | 'imageSliceZ' | 'kind' | 'sourceSliceBytes'
> & {
    texture: ContourTexture;
    textureZ: number;
    extension: { before: number; after: number };
};

type SuggestionRefusal =
    | 'sliceDeformSuggestNotEditable'
    | 'sliceDeformSuggestOutOfDate';

const ESCAPE_OWNER_SELECTOR =
    '[role="dialog"], [role="alertdialog"], [role="menu"], .MuiModal-root, .MuiPopper-root';

const IN_PLANE_ORIGIN_TOLERANCE_VOXELS = 0.01;

/**
 * Subscribes `listener` to every change of which image of a fused pair a
 * view of the edited Environment draws, and returns the unsubscribe.
 */
export type FusedImageDisplaySubscription = (
    listener: () => void
) => () => void;

/**
 * Singleton controller for contour auto-suggest: on a displayed slice
 * where the active structure has no contour, it suggests one — between
 * the structure's first and last contoured slice, a blend of the
 * neighbouring slices refined against the image; beyond them, on any
 * slice of the image, a verbatim copy of the nearest contoured slice.
 *
 * Suggestions are held entirely in-memory until accepted (or
 * discarded by navigation / Esc).
 */
export class ContourAutoSuggestionController {
    private static instance: ContourAutoSuggestionController | undefined;

    public static getInstance(): ContourAutoSuggestionController {
        if (!ContourAutoSuggestionController.instance) {
            ContourAutoSuggestionController.instance =
                new ContourAutoSuggestionController();
        }
        return ContourAutoSuggestionController.instance;
    }

    private enabled = false;
    private environment: Environment | undefined;
    private views: EnvironmentViews | undefined;
    private getRoi: (() => RegionOfInterest | undefined) | undefined;
    private getActiveTool: (() => Tool | undefined) | undefined;

    /**
     * One entry per bound view: a view of the Environment that draws on
     * the image's slices. The value is its renderer, or `undefined` for a
     * view whose 2D overlay could not be drawn on, which stays bound so it
     * is not retried on every re-sync and is never accepted from, since
     * the user sees no ghost there.
     */
    private readonly renderers = new Map<
        PlaneView,
        ContourAutoSuggestionPreviewRenderer | undefined
    >();

    private pending: ActiveSuggestion | undefined;
    private published: PendingContourSuggestion | undefined;
    private pendingTimer: ReturnType<typeof setTimeout> | undefined;

    /** Set while a tool action holds the suggestion off. */
    private reevaluateAfterStroke = false;

    private contourSubscriptions: DomainEventSubscription[] = [];
    private unsubscribeViews: (() => void) | undefined;
    private unsubscribeFusedImageDisplay: (() => void) | undefined;
    private planeIntersectionObserver: Observer<ReadonlyVec3> | undefined;
    private panZoomObserver: Observer<number> | undefined;
    private keydownHandler: ((e: KeyboardEvent) => void) | undefined;
    private releaseHandler: (() => void) | undefined;
    private mousemoveHandler: ((e: MouseEvent) => void) | undefined;

    public readonly pendingSuggestionChanged = new Observable<
        PendingContourSuggestion | undefined
    >();

    private constructor() {}

    public isEnabled(): boolean {
        return this.enabled;
    }

    public hasPendingSuggestion(): boolean {
        return this.pending !== undefined;
    }

    /** The suggestion on offer; the same object until the next notification. */
    public get pendingSuggestion(): PendingContourSuggestion | undefined {
        return this.published;
    }

    /**
     * @param environment The Environment whose structure is edited; the
     *   slice, the pan/zoom and the image are read from it.
     * @param views The live set of views showing that Environment; the
     *   ghost follows the drawable ones as they join and leave.
     * @param subscribeFusedImageDisplay Follows which image of the fused
     *   pair each of those views draws.
     * @param getRoi The structure a suggestion is made for.
     * @param getActiveTool The tool the user is working with, if any.
     */
    public enable(
        environment: Environment,
        views: EnvironmentViews,
        subscribeFusedImageDisplay: FusedImageDisplaySubscription,
        getRoi: () => RegionOfInterest | undefined,
        getActiveTool: () => Tool | undefined
    ): void {
        if (this.enabled) {
            this.disable();
        }
        this.enabled = true;
        this.environment = environment;
        this.views = views;
        this.getRoi = getRoi;
        this.getActiveTool = getActiveTool;

        this.unsubscribeViews = views.subscribe(() => this.syncRenderers());
        this.unsubscribeFusedImageDisplay = subscribeFusedImageDisplay(() =>
            this.syncRenderers()
        );
        this.syncRenderers();

        this.contourSubscriptions = ROI_CONTOUR_EDIT_EVENT_TYPES.map((type) =>
            domainEventBus.subscribe(type, this.onContourEdit.bind(this))
        );

        this.planeIntersectionObserver = (_value: ReadonlyVec3) => {
            this.onPlaneIntersectionChanged();
        };
        environment.planeIntersectionPoint.subscribe(
            this.planeIntersectionObserver
        );

        this.panZoomObserver = (_value: number) => {
            this.refreshRenderers();
        };
        environment.panZoom.subscribe(this.panZoomObserver);

        // Capture phase: a declining Esc is consumed before the engine sees it.
        this.keydownHandler = this.onDocumentKeyDown.bind(this);
        document.addEventListener('keydown', this.keydownHandler, true);
        // Bubble phase: after the engine has ended the tool's action or panned.
        this.releaseHandler = this.resumeAfterStroke.bind(this);
        document.addEventListener('mouseup', this.releaseHandler);
        this.mousemoveHandler = this.onDocumentMouseMove.bind(this);
        document.addEventListener('mousemove', this.mousemoveHandler, false);

        // Evaluate once on enable so a suggestion shows up if the
        // user is already on an empty slice with a populated
        // neighbour, without having to scroll first.
        this.scheduleEvaluation();
    }

    public disable(): void {
        this.enabled = false;
        this.reevaluateAfterStroke = false;

        this.cancelPendingTimer();

        this.contourSubscriptions.forEach((s) => s.unsubscribe());
        this.contourSubscriptions = [];

        if (this.unsubscribeViews) {
            this.unsubscribeViews();
            this.unsubscribeViews = undefined;
        }
        if (this.unsubscribeFusedImageDisplay) {
            this.unsubscribeFusedImageDisplay();
            this.unsubscribeFusedImageDisplay = undefined;
        }

        if (this.environment) {
            if (this.planeIntersectionObserver) {
                this.environment.planeIntersectionPoint.unsubscribe(
                    this.planeIntersectionObserver
                );
            }
            if (this.panZoomObserver) {
                this.environment.panZoom.unsubscribe(this.panZoomObserver);
            }
        }
        this.planeIntersectionObserver = undefined;
        this.panZoomObserver = undefined;

        if (this.keydownHandler) {
            document.removeEventListener('keydown', this.keydownHandler, true);
            this.keydownHandler = undefined;
        }
        if (this.releaseHandler) {
            document.removeEventListener('mouseup', this.releaseHandler);
            this.releaseHandler = undefined;
        }
        if (this.mousemoveHandler) {
            document.removeEventListener(
                'mousemove',
                this.mousemoveHandler,
                false
            );
            this.mousemoveHandler = undefined;
        }

        for (const renderer of this.renderers.values()) {
            renderer?.dispose();
        }
        this.renderers.clear();

        this.offer(undefined);

        this.environment = undefined;
        this.views = undefined;
        this.getRoi = undefined;
        this.getActiveTool = undefined;
    }

    /**
     * Brings the renderers in line with the views that currently show the
     * suggestion ({@link viewShowsAcquiredPrimarySlice}): a view that joined gets a
     * renderer and the pending ghost, a view that left, turned away or
     * turned to the secondary alone has its renderer disposed and is not
     * painted again. Cheap enough to run per signal: one walk of the set
     * and one map lookup per view.
     */
    private syncRenderers(): void {
        if (!this.views) {
            return;
        }
        const drawable = this.views.current.filter(
            viewShowsAcquiredPrimarySlice
        );
        for (const [view, renderer] of this.renderers) {
            if (!drawable.includes(view)) {
                renderer?.dispose();
                this.renderers.delete(view);
            }
        }
        for (const view of drawable) {
            if (this.renderers.has(view)) {
                continue;
            }
            const renderer = this.createRenderer(view);
            this.renderers.set(view, renderer);
            if (renderer && this.pending) {
                renderer.setState(this.pending.preview);
            }
        }
        this.publish();
    }

    private createRenderer(
        view: PlaneView
    ): ContourAutoSuggestionPreviewRenderer | undefined {
        try {
            return new ContourAutoSuggestionPreviewRenderer(view);
        } catch (err) {
            console.warn(
                'ContourAutoSuggestionController: cannot create renderer',
                err
            );
            return undefined;
        }
    }

    private refreshRenderers(): void {
        for (const renderer of this.renderers.values()) {
            renderer?.refresh();
        }
    }

    private ghostViews(): PlaneView[] {
        return [...this.renderers]
            .filter(([, renderer]) => renderer)
            .map(([view]) => view);
    }

    private offer(suggestion: ActiveSuggestion | undefined): void {
        this.pending = suggestion;
        for (const renderer of this.renderers.values()) {
            if (suggestion) {
                renderer?.setState(suggestion.preview);
            } else {
                renderer?.clear();
            }
        }
        this.publish();
    }

    private publish(): void {
        const { pending, published } = this;
        const views = this.ghostViews();
        const unchanged = pending
            ? published?.outlineMm === pending.outlineMm &&
              sameElements(published.views, views)
            : !published;
        if (unchanged) {
            return;
        }
        this.published = pending && {
            outlineMm: pending.outlineMm,
            boundsMm: pending.boundsMm,
            views,
        };
        this.pendingSuggestionChanged.notify(this.published);
    }

    /**
     * Commit the pending suggestion, exactly as shown, as one undoable
     * edit, and trigger a viewport redraw. Returns `true` when written;
     * the pending suggestion is cleared either way. A suggestion the
     * structure no longer yields is refused. The write is staged on a
     * texture of its own, built from the structure's GPU data and
     * extended along Z where the slice lies beyond the structure's, and
     * swapped in once it has landed: the structure's texture is never
     * written, so a failure leaves the structure as it was. Both are
     * reported to the user.
     * Does NOT navigate — the view stays on the current slice.
     */
    public accept(): boolean {
        const offered = this.pending;
        if (!offered) {
            return this.refuse('sliceDeformSuggestOutOfDate', 'warning');
        }
        // A stroke holds snapshots of the texture an accept would replace.
        if (this.getActiveTool?.()?.isStrokeInProgress()) {
            return this.refuse('sliceDeformSuggestNotAdded', 'warning');
        }
        this.offer(undefined);
        const derived = this.revalidate(offered);
        if (typeof derived === 'string') {
            return this.refuse(derived, 'warning');
        }

        const { roi } = offered;
        let write: TextureWrite | undefined;
        try {
            // Before the write is opened: its first-edit snapshot can read
            // back, and the staging's check must cover that readback too.
            discardPendingGlErrors();
            write = roi.startModify(operationOf(offered), {
                restoredOnFailure: true,
            });
            this.stageSuggestion(write, offered, derived);
            write.finish();
        } catch (err) {
            // Nothing read during the failed accept stays cached: a
            // rejected readback is zero-filled.
            derived.texture.invalidateCachedPixels();
            // startModify opens the modification before it snapshots the
            // structure, so one that throws leaves no write to cancel.
            if (write) {
                write.cancel();
            } else {
                roi.resetModificationFlag();
            }
            console.error(
                `ContourAutoSuggestionController: failed to commit suggestion (${errorName(err)})`
            );
            return this.refuse('sliceDeformSuggestNotAdded', 'error');
        }

        ViewManager.invalidate(LayerBit.CONTOUR_LAYER_BIT, roi.boundingBox);
        return true;
    }

    public decline(): void {
        this.offer(undefined);
    }

    /**
     * Stages the suggestion on offer into the edit `write` has open, as one
     * edit with it, and returns whether it did; never for a `view` whose
     * canvas shows none of the ghost. A `write` that is reverted puts the
     * suggestion back on offer if its slice is still displayed.
     */
    public includePendingIn(write: TextureWrite, view: PlaneView): boolean {
        const offered = this.pending;
        if (
            !this.enabled ||
            !offered ||
            write.roi !== offered.roi ||
            !this.renderers.get(view) ||
            !outlineShowsOn(view, offered.outlineMm)
        ) {
            return false;
        }
        const derived = this.revalidate(offered);
        if (typeof derived === 'string') {
            return this.refuse(derived, 'warning');
        }
        try {
            discardPendingGlErrors();
            this.stageSuggestion(write, offered, derived, {
                operation: operationOf(offered),
                onReverted: () => this.onStagingReverted(offered),
            });
        } catch (err) {
            console.error(
                `ContourAutoSuggestionController: failed to include suggestion in an edit (${errorName(err)})`
            );
            return this.refuse('sliceDeformSuggestNotIncluded', 'error');
        }
        this.offer(undefined);
        ViewManager.invalidate(
            LayerBit.CONTOUR_LAYER_BIT,
            offered.roi.boundingBox
        );
        return true;
    }

    private revalidate(
        offered: ActiveSuggestion
    ): DerivedSuggestion | SuggestionRefusal {
        if (!offered.roi.canModifyContours) {
            return 'sliceDeformSuggestNotEditable';
        }
        const current = this.deriveSuggestion();
        return current &&
            current.roi === offered.roi &&
            current.imageSliceZ === offered.imageSliceZ &&
            current.kind === offered.kind &&
            sameBytes(current.sourceSliceBytes, offered.sourceSliceBytes) &&
            sameInPlanePlacement(
                offered.preview.textureGrid,
                current.texture.textureGrid
            )
            ? current
            : 'sliceDeformSuggestOutOfDate';
    }

    private stageSuggestion(
        write: TextureWrite,
        offered: ActiveSuggestion,
        derived: DerivedSuggestion,
        options?: StagedTextureOptions
    ): void {
        const { texture, textureZ, extension } = derived;
        const { before, after } = extension;
        const grid = texture.textureGrid;
        // From the GPU, not the pixel cache, so that the check below covers
        // this readback: WebGL rejects one without throwing and zero-fills it.
        const held = texture.signedDistanceField.toArray();
        assertGlWorkLanded('ContourAutoSuggestionController: read');
        // Every slice keeps its bytes and position; those gained are empty.
        const empty = () => new Uint8Array(offered.sourceSliceBytes.length);
        const slices = [
            ...Array.from({ length: before }, empty),
            ...held,
            ...Array.from({ length: after }, empty),
        ];
        slices[textureZ + before] = offered.sourceSliceBytes.slice();
        const replacement = texture.cloneWithNewSdf(slices, {
            grid:
                before + after
                    ? grid
                          .expandOrShrink(0, 0, before, true)
                          .expandOrShrink(0, 0, after - before, false)
                    : grid,
            adoptPixels: true,
        });
        try {
            // WebGL reports a failed allocation or upload without throwing.
            assertGlWorkLanded('ContourAutoSuggestionController: commit');
            write.stageTexture(replacement, options);
        } catch (err) {
            replacement.delete();
            throw err;
        }
    }

    private onStagingReverted(suggestion: ActiveSuggestion): void {
        // No culling volume: a view outside the restored texture's extent
        // would go on showing the suggestion as contour.
        ViewManager.invalidate(LayerBit.CONTOUR_LAYER_BIT);
        if (
            this.enabled &&
            this.getRoi?.() === suggestion.roi &&
            this.displayedImageSliceZ() === suggestion.imageSliceZ
        ) {
            this.cancelPendingTimer();
            this.reevaluateAfterStroke = false;
            this.offer(suggestion);
        } else {
            this.scheduleEvaluation();
        }
    }

    /** Tells the user an accept wrote nothing, and offers a fresh ghost. */
    private refuse(
        message: CatalogMessageId,
        variant: 'warning' | 'error'
    ): false {
        enqueueSnackbar(localization.t(message), { variant });
        this.scheduleEvaluation();
        return false;
    }

    /**
     * Handle a change to the Environment's plane-intersection point.
     *
     * Plane-intersection updates fire not only on slice scrolls but
     * also on intra-slice movements (crosshair drag, hover-driven
     * snapping, sub-voxel adjustments) and on a view re-oriented in
     * place, so the bound views are re-read first. When the visible
     * slice has NOT changed we keep the current ghost and just repaint
     * it (cheap), so the suggestion does not flicker away on every
     * mouse move. Only an actual slice change tears the ghost down
     * and schedules a fresh evaluation.
     */
    private onPlaneIntersectionChanged(): void {
        if (!this.enabled) {
            return;
        }
        this.syncRenderers();
        if (
            this.pending &&
            this.displayedImageSliceZ() === this.pending.imageSliceZ
        ) {
            this.refreshRenderers();
            return;
        }
        this.scheduleEvaluation();
    }

    /**
     * Document-level mousemove handler (bubble phase). Repaints the ghost,
     * or derives it afresh once the structure's texture is on another grid.
     */
    private onDocumentMouseMove(_e: MouseEvent): void {
        this.resumeAfterStroke();
        if (!this.pending) {
            return;
        }
        if (isOnGridDerivedOn(this.pending)) {
            this.refreshRenderers();
        } else {
            this.evaluateForCurrentSlice();
        }
    }

    /**
     * The plane displayed by the views that show the primary image in its
     * acquisition plane ({@link viewShowsAcquiredPrimarySlice}): through
     * the Environment's plane-intersection point, along such a view's
     * direction. Undefined when no view shows it.
     */
    private displayedPlane(): PlaneMm | undefined {
        const view = this.views?.current.find(viewShowsAcquiredPrimarySlice);
        return view && this.environment
            ? {
                  point: this.environment.planeIntersectionPoint.coordinates,
                  normal: view.viewDirection,
              }
            : undefined;
    }

    /** The displayed slice's index in the image's grid, if it has one. */
    private displayedImageSliceZ(): number | undefined {
        const image = this.environment?.image;
        const plane = this.displayedPlane();
        return image && plane ? resolveImageSlice(image, plane) : undefined;
    }

    /**
     * Document-level keydown handler (capture phase). Consumes the
     * `Esc` key to discard a pending suggestion. When no suggestion
     * is active, the event passes through untouched so existing
     * Esc behaviours (e.g. brush stroke cancellation) keep working.
     *
     * Modifier-only Esc — any combination with Ctrl/Alt/Shift/Meta is
     * ignored to avoid colliding with browser or OS shortcuts.
     */
    private onDocumentKeyDown(e: KeyboardEvent): void {
        if (!this.pending) {
            return;
        }
        if (e.key !== 'Escape') {
            return;
        }
        if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) {
            return;
        }
        if (
            this.isToolActionInProgress() ||
            isTextEntryElement(e.target) ||
            (e.target instanceof Element &&
                e.target.closest(ESCAPE_OWNER_SELECTOR))
        ) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        this.decline();
    }

    /**
     * Derives a suggestion once the tool action that deferred it has ended:
     * on release, or on a later move when the tool did not see the release.
     */
    private resumeAfterStroke(): void {
        if (this.reevaluateAfterStroke && !this.isToolActionInProgress()) {
            this.reevaluateAfterStroke = false;
            this.scheduleEvaluation();
        }
    }

    /**
     * Drop any rendered ghost immediately on a contour edit, undo or
     * redo of the active structure, and schedule a fresh evaluation.
     * Nothing reads the texture here: on undo and redo the structure
     * restores its pixels in its own handler, which may run after this.
     */
    private onContourEdit(event: RoiContourEditEvent): void {
        if (!this.enabled || !this.getRoi) {
            return;
        }
        // ROI numbers are per structure set: gate on the bound ROI's owning
        // set too, so a same-numbered ROI elsewhere never drops the ghost.
        const roi = this.getRoi();
        if (
            !roi ||
            roi.roiNumber !== event.roiNumber ||
            roi.structureSetUid !== event.structureSetUid
        ) {
            return;
        }
        this.scheduleEvaluation();
    }

    /**
     * Cancel any in-flight delay timer and queue a new evaluation.
     * Each slice change resets the timer so rapid scrolling never
     * triggers intermediate ghost renders — the suggestion only
     * appears once the user has stayed on the same slice for the
     * full {@link CONTOUR_AUTO_SUGGESTION_PREVIEW_DELAY_MS} window.
     */
    private scheduleEvaluation(): void {
        if (!this.enabled) {
            return;
        }
        this.cancelPendingTimer();
        // Drop any previously rendered ghost while waiting for the
        // next evaluation — it is bound to a different slice.
        this.offer(undefined);
        this.pendingTimer = setTimeout(() => {
            this.pendingTimer = undefined;
            this.evaluateForCurrentSlice();
        }, CONTOUR_AUTO_SUGGESTION_PREVIEW_DELAY_MS);
    }

    private cancelPendingTimer(): void {
        if (this.pendingTimer !== undefined) {
            clearTimeout(this.pendingTimer);
            this.pendingTimer = undefined;
        }
    }

    /**
     * Derive and render a suggestion for the visible slice; while a tool
     * action is in progress, only once it has ended.
     */
    private evaluateForCurrentSlice(): void {
        if (!this.enabled) {
            return;
        }
        if (this.isToolActionInProgress()) {
            this.offer(undefined);
            this.reevaluateAfterStroke = true;
            return;
        }
        const suggestion = this.deriveSuggestion();
        const grid = suggestion?.texture.textureGrid;
        // Emits vertices in voxel-centered continuous coordinates; the
        // renderer feeds those directly into `Grid.toWorldSpace`, which
        // applies the mm scaling (see extractSlicePolygons).
        const polygons =
            suggestion && grid
                ? extractSlicePolygons(
                      suggestion.sourceSliceBytes,
                      grid.sizeX,
                      grid.sizeY
                  )
                : [];
        const drawn = this.ghostViews().length > 0;
        if (!suggestion || !grid || !drawn || !polygons.length) {
            this.offer(undefined);
            return;
        }
        const { roi, imageSliceZ, kind, sourceSliceBytes } = suggestion;
        const preview: ContourAutoSuggestionPreviewState = {
            polygonsVoxel: polygons,
            targetVoxelZ: suggestion.textureZ,
            textureGrid: grid,
            roiColor: lightenColor(roi.color, 0.15),
        };
        const outlineMm = previewOutlineMm(preview);
        const boundsMm = enclosingBoundsMm(outlineMm);
        const ghost = { preview, outlineMm, boundsMm };
        this.offer({ roi, imageSliceZ, kind, sourceSliceBytes, ...ghost });
    }

    /**
     * The suggestion for the visible slice, if one is offered: beyond
     * the contoured range a copy of the nearest contoured slice, between
     * it a blend of the neighbouring slices. The range is located on
     * the pixel cache where it is populated, and on the GPU during a
     * tool action, which the cache lags; the slices the suggestion is
     * made from are read from the GPU via
     * {@link SignedDistanceField.sliceToArray}.
     */
    private deriveSuggestion(): DerivedSuggestion | undefined {
        const roi = this.getRoi?.();
        const image = this.environment?.image;
        const plane = this.displayedPlane();
        if (!plane || !roi?.canModifyContours || !image) {
            return undefined;
        }
        const texture = roi.texture;
        if (!texture || texture.isEmpty) {
            return undefined;
        }

        const sizeZ = texture.sizePx[2];
        const imageSliceZ = resolveImageSlice(image, plane);
        const resolved = resolveTextureSlice(texture, image, plane);
        if (
            imageSliceZ === undefined ||
            imageSliceZ < 0 ||
            imageSliceZ >= image.parent.grid.sizeZ ||
            resolved.kind === 'notASlicePlane'
        ) {
            return undefined;
        }
        const currentVoxelZ = resolved.sliceZ;

        const sdf = texture.signedDistanceField;
        const readSlice = (z: number): Uint8Array | undefined => {
            if (z < 0 || z >= sizeZ) {
                return undefined;
            }
            try {
                return sdf.sliceToArray(z);
            } catch (err) {
                console.warn(
                    'ContourAutoSuggestionController: sliceToArray failed',
                    err
                );
                return undefined;
            }
        };

        let position: ContouredRangePosition;
        try {
            // During a tool action the GPU runs ahead of the pixel cache.
            const hasContour = this.isToolActionInProgress()
                ? (z: number) =>
                      ContourOperations.sliceHasContour(sdf.sliceToArray(z))
                : ContourOperations.contourPresenceReader(sdf);
            position = ContourOperations.locateInContouredRange(
                currentVoxelZ,
                sizeZ,
                hasContour
            );
        } catch {
            return undefined;
        }
        if (position.kind === 'beyond') {
            const extension = texture.textureGrid.zExtensionToHold(
                currentVoxelZ,
                texture.grid
            );
            const source = readSlice(position.nearestZ);
            return extension && source
                ? {
                      roi,
                      imageSliceZ,
                      kind: 'copied',
                      sourceSliceBytes: source,
                      texture,
                      textureZ: currentVoxelZ,
                      extension,
                  }
                : undefined;
        }
        if (position.kind !== 'between') {
            return undefined;
        }

        // Build the source slice using multi-slice B-spline
        // interpolation. We gather up to 4 non-empty neighbour
        // slices (z-2, z-1, z+1, z+2) and blend them with cubic
        // B-spline weights for smoother transitions compared to
        // the previous linear midpoint blend. When fewer slices
        // are available the function degrades gracefully.
        const candidateOffsets = [-2, -1, 1, 2];
        const neighbourSlices: SliceWithPosition[] = [];

        for (const offset of candidateOffsets) {
            const z = currentVoxelZ + offset;
            const slice = readSlice(z);
            if (slice && ContourOperations.sliceHasContour(slice)) {
                neighbourSlices.push({ data: slice, z });
            }
        }

        let sourceSlice: Uint8Array | undefined;
        if (neighbourSlices.length > 0) {
            try {
                sourceSlice = blendSlicesBSpline(
                    neighbourSlices,
                    currentVoxelZ
                );
            } catch (err) {
                console.warn(
                    'ContourAutoSuggestionController: B-spline blend failed, falling back to nearest copy',
                    err
                );
                // Fall back to the nearest neighbour
                sourceSlice = new Uint8Array(neighbourSlices[0].data);
            }
        }

        if (!sourceSlice) {
            return undefined;
        }

        const grid: Grid = texture.textureGrid;

        // Image-driven refinement: nudge the iso-surface toward
        // anatomical edges visible in the underlying image (works for
        // CT / PET / MR — see deriveRefinementOptions). The B-spline
        // blend gives smooth shape continuity along Z; the intensity-guided
        // passes add local boundary alignment. Failures here are
        // non-fatal — we keep the unrefined B-spline result so the
        // user always sees a preview.
        try {
            const intensitySlice = this.extractIntensitySliceForTexture(
                texture,
                currentVoxelZ
            );
            const refineOptions = intensitySlice
                ? this.deriveRefinementOptions(intensitySlice)
                : undefined;
            if (intensitySlice && refineOptions) {
                // mm→byte scale taken from the structure texture's own
                // quantization margin, so the refinement reflects this
                // SDF's actual encoding rather than a fixed default.
                const sdfBytesPerMm = texture.sdfBytesPerMm();
                // Pass 1: edge-based — pull iso toward intensity
                // gradients (anatomical edges).
                sourceSlice = refineSliceWithIntensityEdge(
                    sourceSlice,
                    intensitySlice,
                    grid.sizeX,
                    grid.sizeY,
                    sdfBytesPerMm,
                    refineOptions.edge
                );
                // Pass 2: region-based — contract/expand where the
                // absolute intensity clearly indicates interior vs
                // exterior (e.g. air inside a lung vs tissue on CT;
                // high uptake vs background on PET).
                sourceSlice = refineSliceWithIntensityRegion(
                    sourceSlice,
                    intensitySlice,
                    grid.sizeX,
                    grid.sizeY,
                    sdfBytesPerMm,
                    refineOptions.region
                );
            }
        } catch (err) {
            console.warn(
                'ContourAutoSuggestionController: intensity refinement failed, using B-spline result as-is',
                err
            );
        }

        return {
            roi,
            imageSliceZ,
            kind: 'interpolated',
            sourceSliceBytes: sourceSlice,
            texture,
            textureZ: currentVoxelZ,
            extension: { before: 0, after: 0 },
        };
    }

    /**
     * Derive the refinement option overrides for the active
     * image's modality, scaling the intensity-unit thresholds so the
     * refinement works across CT / PET / MR rather than only CT.
     *
     *   - **CT** (calibrated Hounsfield Units): use the proven fixed
     *     defaults — return empty overrides.
     *   - **Non-CT** (PET activity/SUV, MR arbitrary units): scale
     *     intensity-unit thresholds via
     *     {@link scaleRefinementOptionsForSpread}.
     *
     * Returns `undefined` when a non-CT slice's intensity spread
     * cannot be characterised, signalling the caller to skip
     * refinement (keep the pure B-spline result) rather than chase
     * noise.
     */
    private deriveRefinementOptions(intensitySlice: Float32Array):
        | {
              edge: Partial<EdgeRefinementOptions>;
              region: Partial<RegionRefinementOptions>;
          }
        | undefined {
        const modality = this.environment?.image?.parent?.firstSlice.modality;

        // CT is calibrated — the intensity defaults are physically tuned.
        if (modality === undefined || modality === Modality.CT) {
            return { edge: {}, region: {} };
        }

        const spread = estimateRobustIntensitySpread(intensitySlice);
        if (spread === undefined) {
            return undefined;
        }

        return scaleRefinementOptionsForSpread(spread);
    }

    /**
     * Resolve the active image's intensity buffer + grid and delegate
     * the grid-aligned sampling to the shared
     * {@link extractIntensitySliceAlignedToGrid} helper (which both
     * this tool and the Interpolation tool use). Returns `undefined`
     * when no image is bound, the image has no float pixel data, the
     * grids are not voxel-aligned, or the target Z is outside the
     * volume.
     *
     * The float-precision intensity buffer lives on the underlying
     * {@link Image3D} (the un-transformed source); using the parent's
     * grid avoids any registration transform on secondary images and
     * keeps indexing consistent with the buffer layout.
     */
    private extractIntensitySliceForTexture(
        texture: ContourTexture,
        targetVoxelZ: number
    ): Float32Array | undefined {
        const image = this.environment?.image;
        const parent = image?.parent;
        if (!parent) {
            return undefined;
        }
        const floatData = parent.floatPixelData;
        if (!floatData) {
            return undefined;
        }
        return extractIntensitySliceAlignedToGrid(
            texture.textureGrid,
            parent.grid,
            floatData,
            targetVoxelZ
        );
    }

    private isToolActionInProgress(): boolean {
        return this.getActiveTool?.()?.isActionInProgress() ?? false;
    }
}

const sameBytes = (a: Uint8Array, b: Uint8Array): boolean =>
    a.length === b.length && a.every((value, i) => value === b[i]);

const sameInPlanePlacement = (drawn: Grid, current: Grid): boolean => {
    const origin = drawn.originInVoxelsOf(current);
    return (
        origin !== undefined &&
        drawn.sizeX === current.sizeX &&
        drawn.sizeY === current.sizeY &&
        Math.abs(origin[0]) <= IN_PLANE_ORIGIN_TOLERANCE_VOXELS &&
        Math.abs(origin[1]) <= IN_PLANE_ORIGIN_TOLERANCE_VOXELS
    );
};

const isOnGridDerivedOn = (suggestion: ActiveSuggestion): boolean => {
    const derivedOn = suggestion.preview.textureGrid;
    const grid = suggestion.roi.builtTexture?.textureGrid;
    return grid === derivedOn || !!grid?.hasEqualGeometryTo(derivedOn);
};

const operationOf = (suggestion: Pick<ActiveSuggestion, 'kind'>): string =>
    suggestion.kind === 'copied'
        ? NEAREST_CONTOUR_COPY_OPERATION
        : CONTOUR_AUTO_SUGGESTION_OPERATION;

function enclosingBoundsMm(
    loops: ReadonlyArray<ReadonlyArray<ReadonlyVec3>>
): WorldBoundsMm {
    const min = vec3.fromValues(Infinity, Infinity, Infinity);
    const max = vec3.fromValues(-Infinity, -Infinity, -Infinity);
    for (const point of loops.flat()) {
        vec3.min(min, min, point);
        vec3.max(max, max, point);
    }
    const [minX, minY, minZ] = min;
    const [maxX, maxY, maxZ] = max;
    return { minX, minY, minZ, maxX, maxY, maxZ };
}
